
import { useState, useEffect, useRef, useCallback } from 'react';
import { InputNumber } from 'antd';

// How long after the last keystroke we push the value up to the parent.
const PROPAGATE_MS = 1000;
// How long after the last keystroke we auto-format to 2 decimals while still focused.
const FORMAT_IDLE_MS = 5000;

// Turn whatever the user typed into something Number() understands.
// "12,5" -> "12.5", "1.234,56" -> "1234.56", "1,234.56" -> "1234.56"
export const normalize = (val) => {
  if (val === null || val === undefined) return '';
  let str = String(val).replace(/\s/g, '');
  const comma = str.lastIndexOf(',');
  const dot = str.lastIndexOf('.');
  if (comma >= 0 && dot >= 0) {
    // The last separator is the decimal one, the other is thousands grouping.
    str = comma > dot
      ? str.replace(/\./g, '').replace(',', '.')
      : str.replace(/,/g, '');
  } else {
    str = str.replace(/,/g, '.');
  }
  return str;
};

const format = (val) => {
  if (val === null || val === undefined || val === '') return '';
  const num = Number(normalize(val));
  return isNaN(num) ? String(val) : num.toFixed(2);
};

const DebouncedNumberInput = ({ value: propValue, onChange: propOnChange, disabled, placeholder, size = null, status, min = 0 }) => {
  const [text, setText] = useState(() => format(propValue));
  // While editing, we hand the raw typed text back to the input untouched.
  //
  // A ref, not state, on purpose. rc-input-number calls our onChange and then,
  // in the SAME tick, re-formats the text through `formatter` while the field
  // is still uncontrolled (InputNumber.js:275-280 — it is uncontrolled because
  // `value` below is undefined while `text` is empty). A queued setState would
  // not have committed by then, so the formatter would still see editing=false
  // and paint "5" as "5.00" on the very first keystroke of an empty field.
  const editingRef = useRef(false);
  const propagateTimeout = useRef(null);
  const formatTimeout = useRef(null);
  const pending = useRef(null);
  // External value that arrived while the user was typing (e.g. the server
  // rounding tax_amount). Applied once we stop editing.
  const pendingExternal = useRef(undefined);

  const clearTimers = () => {
    if (propagateTimeout.current) clearTimeout(propagateTimeout.current);
    if (formatTimeout.current) clearTimeout(formatTimeout.current);
    propagateTimeout.current = null;
    formatTimeout.current = null;
  };

  useEffect(() => clearTimers, []);

  // Sync with external value changes (controlled usage), but never while the
  // user is typing — that echo is what used to overwrite "12" with "12.00".
  useEffect(() => {
    if (propValue === undefined) return;
    if (editingRef.current) {
      pendingExternal.current = propValue;
      return;
    }
    pendingExternal.current = undefined;
    setText(format(propValue));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propValue]);

  // Leave editing mode: show 2 decimals, preferring an external value that
  // arrived (and was ignored) while typing.
  const settle = () => {
    editingRef.current = false;
    const external = pendingExternal.current;
    pendingExternal.current = undefined;
    setText((prev) => format(external === undefined || external === null ? prev : external));
  };

  const propagate = () => {
    if (pending.current === null) return;
    const strVal = pending.current;
    pending.current = null;
    if (propOnChange) {
      propOnChange(strVal === '' ? null : Number(strVal));
    }
  };

  const handleChange = (val) => {
    const strVal = val === null || val === undefined ? '' : normalize(val);
    clearTimers();
    editingRef.current = true;
    setText(strVal);
    pending.current = strVal;
    // A fresh keystroke invalidates any echo we received earlier.
    pendingExternal.current = undefined;

    propagateTimeout.current = setTimeout(propagate, PROPAGATE_MS);
    formatTimeout.current = setTimeout(settle, FORMAT_IDLE_MS);
  };

  // Blur / Enter: commit right away and show the final 2-decimal value.
  const commit = () => {
    clearTimers();
    propagate();
    settle();
  };

  // Typing a "," rewrites it to "." straight away: pushing the normalized text
  // into `text` makes rc-input-number re-run the formatter below.
  const handleInput = (raw) => {
    if (typeof raw === 'string' && raw.includes(',')) setText(normalize(raw));
    // Keystrokes that don't change the value (a lone ".", a trailing "0")
    // should still count as activity for the idle timer.
    if (editingRef.current) {
      if (formatTimeout.current) clearTimeout(formatTimeout.current);
      formatTimeout.current = setTimeout(settle, FORMAT_IDLE_MS);
    }
  };

  // Stable identity. rc-input-number repaints with userTyping=false whenever the
  // formatter changes identity (InputNumber.js:451-455); tying that to `editing`
  // meant every start of typing triggered a reformat. The 2-decimal repaint on
  // settle/blur instead rides on the `value` prop below, which settle() changes
  // ("557" -> "557.00"), firing InputNumber.js:458-469.
  const formatter = useCallback((val, info) => {
    const typed = normalize(info?.input ?? '');
    // While editing, keep the user's own text (with "," swapped for ".") —
    // but only while it still represents the current value. A stepper click
    // or an external update must show the real number instead.
    if (editingRef.current && typed !== '' && Number(typed) === Number(normalize(val))) return typed;
    return format(val);
  }, []);

  return (
    <InputNumber
      size={size}
      addonAfter="€"
      value={text === '' ? undefined : text}
      onChange={handleChange}
      onInput={handleInput}
      onBlur={commit}
      onPressEnter={commit}
      formatter={formatter}
      parser={normalize}
      precision={2}
      step={0.01}
      min={min}
      placeholder={placeholder}
      disabled={disabled}
      status={status}
      stringMode
    />
  );
};

export default DebouncedNumberInput;
